// Top-level game controller: title → connect → render loop.
//
// The client is a renderer of interpolated server state (architecture §8.5):
// input intent goes up, snapshots come down, nothing is simulated locally.

import { tuning } from '@evox/shared';
import { createStage } from './render/stage.js';
import { createCamera, updateCamera, applyCameraToLayer } from './render/camera.js';
import { drawGrid } from './render/grid.js';
import { drawEntities, drawBouncers } from './render/entities.js';
import { drawPathPreview } from './render/indicator.js';
import { createHud } from './render/hud.js';
import { createKeyboardInput } from './input/keyboard.js';
import { createTouchInput } from './input/touch.js';
import { connect, type Connection } from './net/connection.js';
import { nearestCopy } from './render/geometry.js';
import { showTitleScreen, recordBestSpeed } from './ui/title.js';
import { playPelletEat, unlockAudio } from './audio/sfx.js';

function wsUrl(): string {
  const fromEnv = import.meta.env.VITE_WS_URL as string | undefined;
  if (fromEnv) return fromEnv;
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  // Dev (vite on :5173): game server runs separately on :8080.
  if (import.meta.env.DEV) return `ws://${location.hostname}:8080`;
  // Production default: same origin — the server also serves the client.
  return `${proto}://${location.host}`;
}

/** Client-side cap on queued turns (matches the server cap). */
const MAX_QUEUE = 5;
/**
 * Hold-to-enqueue pacing (player request 2026-06-12): the trail should grow
 * only slightly faster than the player consumes it. Enqueue rate =
 * speed (matches consumption) + a small constant surplus, so the planned
 * route stays a controllable arm's length ahead at any speed.
 */
const HOLD_SURPLUS_PER_SECOND = 0.7;

export async function startGame(root: HTMLElement): Promise<void> {
  const { name } = await showTitleScreen(root);
  unlockAudio(); // user gesture just happened — safe to create AudioContext

  let conn = await connectWithStatus(root, name);
  const stage = await createStage(root);
  const hud = createHud(root);
  const keyboard = createKeyboardInput();
  const touch = createTouchInput(root);

  let camera = createCamera();
  let cameraInit = false;
  let lastTime: number | undefined;
  let holdAccumulator = 0;
  let sentUnacked = 0;
  let lastLocalSpeed = 0;
  let lastSnapTick = -1;
  let wasInvuln = false;
  let running = true;

  // Reconnect with the resume token if the socket drops (GDD §16).
  const installReconnect = (c: Connection): void => {
    c.onClose(() => {
      if (!running) return;
      void (async () => {
        const overlay = statusOverlay(root, 'reconnecting…');
        for (let attempt = 0; attempt < 5; attempt++) {
          try {
            conn = await connect(wsUrl(), name, c.resumeToken);
            installReconnect(conn);
            overlay.remove();
            return;
          } catch {
            await sleep(1200);
          }
        }
        overlay.textContent = 'connection lost — reload to play again';
      })();
    });
  };
  installReconnect(conn);

  function loop(now: number): void {
    const dt = lastTime !== undefined ? (now - lastTime) / 1000 : 0;
    lastTime = now;

    // Turn queue input: a tap enqueues one turn; holding keeps enqueueing
    // at a speed-proportional rate. Input only ever APPENDS to the plan.
    const meNow = conn.latest()?.players.find((p) => p.id === conn.localPlayerId);
    const queueLen = (meNow?.pendingTurns.length ?? 0) + sentUnacked;
    for (const tap of [...keyboard.drainTaps(), ...touch.drainTaps()]) {
      if (queueLen + 1 <= MAX_QUEUE) {
        conn.sendTurn(tap);
        sentUnacked++;
      }
    }
    const held = keyboard.getHeld() ?? touch.getHeld();
    if (held && meNow) {
      holdAccumulator += dt * (meNow.speed + HOLD_SURPLUS_PER_SECOND);
      while (holdAccumulator >= 1) {
        holdAccumulator -= 1;
        if ((meNow.pendingTurns.length + sentUnacked) < MAX_QUEUE) {
          conn.sendTurn(held);
          sentUnacked++;
        }
      }
    } else {
      holdAccumulator = 0;
    }

    const view = conn.getView(now);
    if (view) {
      const me = view.players.find((p) => p.id === conn.localPlayerId);
      if (me) {
        const target = cameraInit
          ? nearestCopy(me.px, { x: camera.x, y: camera.y }, conn.grid.dims)
          : me.px;
        camera = cameraInit ? updateCamera(camera, target.x, target.y, 0, 0, dt) : { ...camera, x: target.x, y: target.y };
        cameraInit = true;
      }

      applyCameraToLayer(camera, stage.worldLayer);
      applyCameraToLayer(camera, stage.entitiesLayer);
      drawGrid(stage.worldLayer, conn.grid);
      drawBouncers(stage.bouncerLayer, conn.grid, conn.bouncers, { x: camera.x, y: camera.y });
      applyCameraToLayer(camera, stage.bouncerLayer);
      drawEntities(stage.entitiesLayer, conn.grid, view, conn.localPlayerId, { x: camera.x, y: camera.y }, now);
      // The trail IS the plan: draw the authoritative queued route.
      const meSnap = conn.latest()?.players.find((p) => p.id === conn.localPlayerId);
      drawPathPreview(stage.entitiesLayer, conn.grid, meSnap, conn.bouncers, { x: camera.x, y: camera.y });
    }

    // Snapshot-edge effects: pellet pop + best-speed + respawn flash.
    const snap = conn.latest();
    if (snap && snap.tick !== lastSnapTick) {
      lastSnapTick = snap.tick;
      sentUnacked = 0; // fresh authoritative queue state arrived
      const me = snap.players.find((p) => p.id === conn.localPlayerId);
      if (me) {
        if (me.speed > lastLocalSpeed + tuning.pelletSpeedGain * 0.45) {
          playPelletEat(me.speed);
        }
        recordBestSpeed(me.speed);
        if (me.invuln && !wasInvuln && lastLocalSpeed > 0) flashRespawn(root);
        wasInvuln = me.invuln;
        lastLocalSpeed = me.speed;
      }
      hud.update(snap, conn.localPlayerId);
    }

    requestAnimationFrame(loop);
  }

  requestAnimationFrame(loop);
}

async function connectWithStatus(root: HTMLElement, name: string): Promise<Connection> {
  const overlay = statusOverlay(root, 'connecting…');
  try {
    const conn = await connect(wsUrl(), name);
    overlay.remove();
    return conn;
  } catch (err) {
    overlay.textContent = 'could not reach the server — is it running?';
    throw err;
  }
}

/** Brief "Again." dim on respawn (GDD §14.3) — no buttons, no tips. */
function flashRespawn(root: HTMLElement): void {
  const el = document.createElement('div');
  el.textContent = 'Again.';
  el.style.cssText = `position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.6);color:#e6ecff;font-size:34px;font-family:system-ui,sans-serif;letter-spacing:2px;z-index:5;pointer-events:none;transition:opacity .5s;`;
  root.appendChild(el);
  setTimeout(() => {
    el.style.opacity = '0';
    setTimeout(() => el.remove(), 600);
  }, 900);
}

function statusOverlay(root: HTMLElement, text: string): HTMLElement {
  const el = document.createElement('div');
  el.textContent = text;
  el.style.cssText = `position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);color:#8b96ad;font-size:18px;font-family:system-ui,sans-serif;z-index:8;`;
  root.appendChild(el);
  return el;
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
